import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';

import {
  fetchPublicKey,
  saveSubscription,
  saveUserPreferences,
  fetchSubscriptionStatus,
  fetchUserPreferences,
  urlBase64ToUint8Array,
} from '../services/notification-service';
import type { PushSubscriptionJSON } from '../services/types';
const whatjobsLogo = new URL('../../whatjobs-logo.svg', import.meta.url).href;
const whatjobsLogoWhite = new URL('../../whatjobs-logo-white.png', import.meta.url).href;
const WELCOME_TIMINGS = {
  fadeDuration: 640,
  fadeDelayStep: 220,
  loadingDuration: 4000,
  appIntroDuration: 900,
  notificationSuccessDuration: 1800,
} as const;
const ALERTS_PROMPT_STORAGE_KEY = 'wj-alerts-prompt';
const ALERTS_PROMPT_INITIAL_DELAY_MS = 12000;
const ALERTS_PROMPT_COOLDOWN_MS = 24 * 60 * 60 * 1000;
type AlertsPromptState = {
  snoozeUntil?: number;
  permanentlyDismissed?: boolean;
};

@customElement('app-home')
export class AppHome extends LitElement {
  @state() private installPrompt: any = null;
  @state() private showInstallButton = false;
  @state() private isOffline = !navigator.onLine;
  @state() private iframeFailed = false;
  @state() private notificationsSupported =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  @state() private notificationPermission: NotificationPermission = Notification.permission;
  @state() private isProcessingNotification = false;
  @state() private notificationError: string | null = null;
  @state() private iframeSrc = 'https://www.whatjobs.com/';
  // @state() private iframeSrc = 'https://kut-kut-www-country-country-us.aj.dev.whatjobs.com/';
  @state() private showWelcomeScreen = false;
  @state() private showPersonalizePrompt = false;
  @state() private showAlertsPrompt = false;
  @state()
  private welcomeStage: 'intro' | 'jobDetails' | 'alerts' | 'notificationsSuccess' | 'loading' | 'employerComingSoon' = 'intro';
  @state() private appIntroActive = false;
  @state() private jobTitle = '';
  @state() private jobLocation = '';
  @state() private alertsOptIn = false;
  @state() private jobDetailsError: string | null = null;

  // --- SPLASH SCREEN STATE ---
  @state() private showSplashScreen = true;
  @state() private splashFading = false;
  @state() private welcomeAnimActive = false;
  @state() private showExternalLaunchNotice = false;

  private readonly onlineHandler = () => this.handleOnline();
  private readonly offlineHandler = () => this.handleOffline();
  private readonly messageHandler = (event: MessageEvent) => this.handleMessage(event);
  private promptTimeoutId: number | null = null;
  private setupTimeoutId: number | null = null;
  private appIntroTimeoutId: number | null = null;
  private notificationSuccessTimeoutId: number | null = null;
  private alertsPromptTimeoutId: number | null = null;
  private clientId: string | null = null;
  private shouldPromptForAlerts = false;
  private externalLaunchTimeoutId: number | null = null;

  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
      min-height: 100dvh;
      overflow: hidden;
    }

    .container {
      display: flex;
      flex-direction: column;
      height: 100vh;
      min-height: 100dvh;
      max-height: 100dvh;
      position: relative;
      overflow: hidden;
      min-width: 0;
      max-width: 100vw;
    }

    /* --- SPLASH SCREEN STYLES --- */
    /* --- SPLASH SCREEN STYLES --- */
    .splash-screen {
      position: fixed;
      inset: 0;
      background: #ff2626; /* Changed to requested color */
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      transition: opacity 500ms cubic-bezier(0.4, 0, 0.2, 1),
                  visibility 500ms linear,
                  transform 500ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    .splash-screen.fade-out {
      opacity: 0;
      visibility: hidden;
      transform: scale(1.05);
      pointer-events: none;
    }

    .splash-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      position: relative;
      width: 100%;
    }

    .splash-logo {
      width: 180px;
      max-width: 60vw;
      opacity: 0;
      transform: translateY(20px);
      animation: logo-entrance 1000ms cubic-bezier(0.19, 1, 0.22, 1) forwards;
      filter: drop-shadow(0 10px 25px rgba(0, 0, 0, 0.2)); /* Slightly darker shadow for contrast */
    }

    .splash-loader {
      width: 140px;
      height: 4px;
      background-color: rgba(255, 255, 255, 0.25); /* Changed to transparent white */
      border-radius: 4px;
      margin-top: 2.5rem;
      overflow: hidden;
      position: relative;
      opacity: 0;
      animation: fade-in 600ms ease forwards 400ms;
    }

    .splash-loader::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      width: 40%;
      background: #ffffff; /* Changed to solid white */
      border-radius: 4px;
      animation: loader-shimmer 1.5s infinite ease-in-out;
    }

    @keyframes logo-entrance {
      0% { opacity: 0; transform: translateY(20px) scale(0.95); }
      100% { opacity: 1; transform: translateY(0) scale(1); }
    }

    @keyframes loader-shimmer {
      0% { transform: translateX(-150%); }
      50% { transform: translateX(120%); }
      100% { transform: translateX(350%); }
    }

    @keyframes fade-in {
      to { opacity: 1; }
    }
    /* --------------------------- */

    .app-main {
      flex: 1 1 auto;
      min-height: 0;
      min-width: 0;
      border: none;
      transition: opacity 240ms ease, transform 240ms ease;
      width: 100%;
      max-width: 100vw;
      overflow: hidden;
    }

    .app-main--intro {
      opacity: 0;
      animation: app-main-in ${WELCOME_TIMINGS.appIntroDuration}ms ease forwards;
    }

    iframe {
      height: 100%;
      width: 100%;
      border: none;
      display: block;
    }

    .external-launch {
      position: fixed;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.55);
      z-index: 10000;
      backdrop-filter: blur(4px);
    }

    .external-launch__card {
      background: #101820;
      color: #f8fafc;
      padding: 1.25rem 1.5rem;
      border-radius: 16px;
      min-width: 260px;
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
      display: grid;
      gap: 0.35rem;
      text-align: center;
      border: 1px solid rgba(255, 255, 255, 0.08);
    }

    .external-launch__spinner {
      width: 36px;
      height: 36px;
      margin: 0 auto 0.35rem;
      border-radius: 50%;
      border: 4px solid rgba(255, 255, 255, 0.18);
      border-top-color: #ffffff;
      animation: spinner-rotate 0.9s linear infinite;
    }

    .external-launch__title {
      margin: 0;
      font-weight: 700;
      font-size: 1.05rem;
      letter-spacing: 0.01em;
    }

    .external-launch__subtitle {
      margin: 0;
      color: #cbd5e1;
      font-size: 0.95rem;
    }

    .install-header,
    .update-header {
      background: linear-gradient(90deg, #e42b2b 0%, #c71111 50%, #9b0c0c 100%);
      color: #fff;
      padding: 0.9rem 1.25rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;
      border-bottom: 1px solid rgba(0, 0, 0, 0.25);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
      flex-wrap: wrap;
      width: 100%;
      box-sizing: border-box;
    }

    .install-copy {
      display: flex;
      flex-direction: column;
      gap: 0.15rem;
      margin: 0;
    }

    .install-title {
      margin: 0;
      font-size: 1.05rem;
      font-weight: 700;
      letter-spacing: 0.01em;
    }

    .install-subtitle {
      margin: 0;
      font-size: 0.95rem;
      opacity: 0.92;
      line-height: 1.4;
    }

    .install-actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
      margin-left: auto;
    }

    .install-actions sl-button::part(base) {
      padding: 0.6rem 1.1rem;
      font-weight: 700;
      font-size: 0.95rem;
    }

    .install-primary::part(base) {
      background: #0f0f10;
      color: #ffffff;
      border: 1px solid #0f0f10;
      border-radius: 6px;
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.35);
      letter-spacing: 0.01em;
    }

    .install-primary::part(base):hover {
      background: #1a1a1c;
      border-color: #1a1a1c;
    }

    .install-secondary::part(base) {
      background: transparent;
      color: #ffffff;
      border: 1px solid rgba(255, 255, 255, 0.7);
      border-radius: 6px;
      box-shadow: none;
    }

    .install-secondary::part(base):hover {
      background: rgba(255, 255, 255, 0.12);
      border-color: #ffffff;
    }

    @media (max-width: 720px) {
      .install-header,
      .update-header {
        align-items: flex-start;
      }

      .install-actions {
        width: 100%;
        justify-content: flex-start;
      }

      .install-actions sl-button {
        flex: 1 1 auto;
      }
    }

    @media (max-width: 520px) {
      .install-header,
      .update-header {
        padding: 0.75rem 1rem;
      }

      .install-title {
        font-size: 1rem;
      }

      .install-subtitle {
        font-size: 0.9rem;
      }

      .install-actions {
        gap: 0.4rem;
      }

      .install-actions sl-button {
        width: 100%;
      }
    }

    .offline-panel {
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 2.5rem 1.5rem;
      text-align: center;
      background: #f3f6fb;
      border-top: 1px solid #d5e3f6;
      gap: 1rem;
    }

    .offline-panel h2 {
      color: black;
      margin: 0;
      font-size: 1.5rem;
    }

    .offline-panel p {
      margin: 0;
      color: #4b5874;
    }

    .app-main[hidden] {
      display: none;
    }

    .welcome-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #ffffff;
      padding: 2rem 1.5rem 3rem;
      z-index: 10;
      box-sizing: border-box;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }

    .welcome-content {
      max-width: 480px;
      width: 100%;
      text-align: center;
    }

    .welcome-logo {
      width: 240px;
      max-width: 100%;
      margin: 0 auto 3rem;
      display: block;
    }

    .welcome-headline {
      font-size: 1.9rem;
      font-weight: 600;
      margin: 0 0 0.75rem;
      color: #101820;
    }

    .welcome-subheadline {
      font-size: 1.1rem;
      color: #475569;
      margin: 0 0 2.5rem;
    }

    .welcome-actions {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .welcome-action {
      border: 1px solid rgba(15, 23, 42, 0.16);
      border-radius: 16px;
      padding: 1rem 1.25rem;
      background: #ffffff;
      font-size: 1.05rem;
      font-weight: 500;
      color: #0f172a;
      cursor: pointer;
      transition: border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease;
    }

    .welcome-action:focus-visible {
      outline: 3px solid #3498db;
      outline-offset: 2px;
    }

    .welcome-action:hover {
      border-color: rgba(15, 23, 42, 0.32);
      box-shadow: 0 12px 24px rgba(15, 23, 42, 0.08);
      transform: translateY(-1px);
    }

    .welcome-action:active {
      transform: translateY(0);
    }

    .welcome-content--form {
      max-width: 520px;
    }

    .welcome-form {
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
      margin: 2.5rem 0;
      text-align: left;
    }

    .field {
      display: flex;
      flex-direction: column;
      gap: 0.45rem;
    }

    .field label {
      font-size: 0.95rem;
      font-weight: 600;
      color: #1e293b;
    }

    .field input {
      border-radius: 14px;
      border: 1px solid rgba(15, 23, 42, 0.18);
      padding: 0.85rem 1rem;
      font-size: 1rem;
      transition: border-color 120ms ease, box-shadow 120ms ease;
    }

    .field input:focus {
      outline: none;
      border-color: #3498db;
      box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.18);
    }

    .form-actions {
      display: flex;
      justify-content: center;
      gap: 1rem;
    }

    .primary-button,
    .secondary-button {
      border-radius: 999px;
      padding: 0.85rem 2.5rem;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      transition: transform 120ms ease, box-shadow 120ms ease;
    }

    .primary-button {
      background: #3498db;
      color: #fff;
      border: none;
      box-shadow: 0 12px 24px rgba(52, 152, 219, 0.22);
    }

    .primary-button:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      box-shadow: none;
    }

    .primary-button:not(:disabled):hover {
      transform: translateY(-1px);
      box-shadow: 0 16px 28px rgba(52, 152, 219, 0.28);
    }

    .secondary-button {
      background: transparent;
      color: #1f2937;
      border: 1px solid rgba(15, 23, 42, 0.2);
    }

    .secondary-button:hover {
      transform: translateY(-1px);
      box-shadow: 0 8px 16px rgba(15, 23, 42, 0.12);
    }

    .toggle-card {
      border-radius: 20px; /* Slightly rounder for a friendlier feel */
      border: 1px solid rgba(15, 23, 42, 0.08); /* Softer border */
      padding: 1.75rem 1.5rem;
      background: #ffffff; /* crisper white background usually looks cleaner than gray for main cards */
      /* Or keep #f8fafc if you prefer slight gray, but white often pops better against the modal overlay */
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin-bottom: 2rem;
      text-align: left;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03); /* Subtle depth */
    }

    .toggle-row {
      display: flex;
      justify-content: space-between;
      align-items: center; /* Center toggle vertically with the text block */
      gap: 1rem;
    }

    .toggle-row > div {
      flex: 1; /* Allow text to take available space */
      padding-right: 0.5rem; /* spacing between text and toggle */
    }

    .toggle-row h2 {
      margin: 0 0 0.35rem 0; /* Tighten spacing to subtitle */
      font-size: 1.25rem;
      font-weight: 700;
      color: #0f172a; /* Darker, sharper color */
      line-height: 1.2;
    }

     .toggle-row p {
      margin: 0;
      color: #64748b; /* Muted slate color for subtitle */
      font-size: 0.95rem;
      line-height: 1.4;
    }

    .toggle-switch {
      position: relative;
      width: 52px; /* Slightly more compact standard width */
      height: 30px;
      flex-shrink: 0; /* Prevent squashing */
    }

    .toggle-switch input {
      opacity: 0;
      width: 0;
      height: 0;
    }

    .toggle-slider {
      position: absolute;
      cursor: pointer;
      inset: 0;
      background-color: rgba(15, 23, 42, 0.2);
      transition: background-color 180ms ease;
      border-radius: 999px;
    }

    .toggle-slider::before {
      content: '';
      position: absolute;
      height: 24px;
      width: 24px;
      left: 3px;
      top: 3px;
      background-color: #fff;
      border-radius: 50%;
      transition: transform 180ms ease;
      box-shadow: 0 6px 12px rgba(15, 23, 42, 0.18);
    }

    .toggle-switch input:checked + .toggle-slider {
      background-color: #3498db;
    }

    .toggle-switch input:checked + .toggle-slider::before {
      transform: translateX(22px); /* Adjust travel distance (52 - 3 - 3 - 24 = 22) */
    }

    .toggle-support-note {
      font-size: 0.9rem;
      color: #ef4444;
      margin-bottom: 2rem;
      display: block;
    }

    .notification-error {
      margin: 1rem 0 2rem 0;
      color: #b91c1c;
      background: rgba(239, 68, 68, 0.1);
      border-radius: 12px;
      padding: 0.75rem 1rem;
      font-size: 0.95rem;
    }

    .notification-progress {
      display: inline-flex;
      align-items: center;
      gap: 0.6rem;
      color: #0f172a;
      font-size: 0.95rem;
      margin-top: 0.75rem;
    }

    .progress-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #3498db;
      animation: pulse 1s ease-in-out infinite;
    }

    @keyframes pulse {
      0%,
      100% {
        transform: scale(1);
        opacity: 0.65;
      }
      50% {
        transform: scale(1.4);
        opacity: 1;
      }
    }

    .success-content {
      max-width: 420px;
      width: 100%;
      text-align: center;
    }

    .success-badge {
      width: 120px;
      height: 120px;
      margin: 0 auto 2rem;
      border-radius: 50%;
      border: 6px solid rgba(52, 152, 219, 0.25);
      position: relative;
      animation: badge-pop 480ms ease forwards;
    }

    .success-badge::after {
      content: '';
      position: absolute;
      inset: 18px;
      background: #2ecc71;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: badge-fill 520ms ease forwards 160ms;
    }

    .success-check {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transform: scale(0.6);
      z-index: 5;
      animation: check-reveal 420ms cubic-bezier(0.2, 0.9, 0.35, 1.2) forwards 320ms;
    }

    .success-check svg {
      width: 44px;
      height: 44px;
    }

    .success-title {
      color: #0f172a;
      margin: 0 0 0.75rem;
      font-size: 1.5rem;
      font-weight: 600;
    }

    .success-copy {
      margin: 0;
      color: #475569;
      font-size: 1rem;
    }

    @media (max-width: 480px) {
      .welcome-headline {
        font-size: 1.6rem;
      }

      .welcome-subheadline {
        font-size: 1rem;
      }

      .welcome-action {
        font-size: 1rem;
      }

      .form-actions {
        flex-direction: column;
      }

      .primary-button,
      .secondary-button {
        width: 100%;
      }
    }

    .setup-content {
      max-width: 420px;
      width: 100%;
      text-align: center;
    }

    .setup-spinner {
      width: 64px;
      height: 64px;
      margin: 0 auto 2rem;
      border-radius: 50%;
      border: 5px solid rgba(52, 152, 219, 0.18);
      border-top-color: #3498db;
      animation: spinner-rotate 0.9s linear infinite;
    }

    .setup-title {
      margin: 0 0 1.25rem;
      color: #0f172a;
      font-size: 1.35rem;
      font-weight: 600;
    }

    .setup-steps {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 0.8rem;
      color: #475569;
      font-size: 1rem;
    }

    .setup-step {
      padding: 0.75rem 1rem;
      border-radius: 12px;
      background: rgba(15, 23, 42, 0.05);
    }

    .fade-in {
      opacity: 0; /* Hidden by default */
      transform: translateY(12px);
      /* Animation removed from here, moved to below */
    }


    /* Only run animation when the parent has the 'anim-start' class */
    .anim-start .fade-in {
      animation: fade-in-up ${WELCOME_TIMINGS.fadeDuration}ms ease forwards;
    }

    /* Delays also need to be scoped or they won't work nicely */
    .anim-start .fade-in--delay-1 {
      animation-delay: ${WELCOME_TIMINGS.fadeDelayStep}ms;
    }

    .anim-start .fade-in--delay-2 {
      animation-delay: ${WELCOME_TIMINGS.fadeDelayStep * 2}ms;
    }

    .anim-start .fade-in--delay-3 {
      animation-delay: ${WELCOME_TIMINGS.fadeDelayStep * 3}ms;
    }

    .anim-start .fade-in--delay-4 {
      animation-delay: ${WELCOME_TIMINGS.fadeDelayStep * 4}ms;
    }

    .fade-in--delay-1 {
      animation-delay: ${WELCOME_TIMINGS.fadeDelayStep}ms;
    }

    .fade-in--delay-2 {
      animation-delay: ${WELCOME_TIMINGS.fadeDelayStep * 2}ms;
    }

    .fade-in--delay-3 {
      animation-delay: ${WELCOME_TIMINGS.fadeDelayStep * 3}ms;
    }

    .fade-in--delay-4 {
      animation-delay: ${WELCOME_TIMINGS.fadeDelayStep * 4}ms;
    }

    .welcome-content-p {
      color: #94a3b8; /* Lighter color for 'fine print' feel */
      font-size: 0.85rem; /* Smaller size */
      line-height: 1.4;
      margin-top: 0.25rem;
      padding-top: 1rem;
      border-top: 1px solid #f1f5f9; /* Subtle divider to separate action from disclaimer */
    }

    .personalize-prompt {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      background: #1f2937;
      color: #ffffff;
      padding: 1rem 1.25rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      z-index: 8;

      /* Start hidden (pushed down) */
      transform: translateY(100%);

      /* Smooth slide-in animation */
      transition: transform 500ms cubic-bezier(0.16, 1, 0.3, 1);

      box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
      will-change: transform;
    }

    /* When the data-open attribute is present, slide up */
    .personalize-prompt[data-open] {
      transform: translateY(0);
    }

    .personalize-text {
      font-size: 0.95rem;
      font-weight: 500;
      margin: 0;
    }

    .personalize-actions {
      display: flex;
      gap: 0.75rem;
    }

    .personalize-actions sl-button::part(base) {
       font-size: 0.9rem;
       height: auto;
       min-height: 2rem;
       padding: 0.4rem 0.8rem;
    }

    .alerts-prompt {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      background: #0f172a;
      color: #ffffff;
      padding: 1rem 1.25rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      z-index: 7;
      transform: translateY(120%);
      transition: transform 480ms cubic-bezier(0.16, 1, 0.3, 1);
      box-shadow: 0 -6px 16px rgba(0, 0, 0, 0.2);
      flex-wrap: wrap;
    }

    .alerts-prompt[data-open] {
      transform: translateY(0);
    }

    .alerts-copy {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      flex: 1 1 auto;
      min-width: 180px;
    }

    .alerts-title {
      margin: 0;
      font-size: 1rem;
      font-weight: 700;
    }

    .alerts-subtitle {
      margin: 0;
      font-size: 0.92rem;
      opacity: 0.9;
    }

    .alerts-actions {
      display: flex;
      gap: 0.5rem;
      align-items: center;
      flex-wrap: wrap;
    }

    .alerts-actions sl-button::part(base) {
      font-size: 0.9rem;
      padding: 0.45rem 1rem;
      min-height: 2.25rem;
    }

    @keyframes app-main-in {
      0% {
        opacity: 0;
        transform: translateY(16px);
      }
      100% {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes fade-in-up {
      0% {
        opacity: 0;
        transform: translateY(18px);
      }
      100% {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes spinner-rotate {
      to {
        transform: rotate(360deg);
      }
    }

    @keyframes badge-pop {
      0% {
        transform: scale(0.5);
        opacity: 0;
      }
      100% {
        transform: scale(1);
        opacity: 1;
      }
    }

    @keyframes badge-fill {
      0% {
        transform: scale(0.4);
        opacity: 0;
      }
      100% {
        transform: scale(1);
        opacity: 1;
      }
    }

    @keyframes check-reveal {
      0% {
        opacity: 0;
        transform: scale(0.6);
      }
      100% {
        opacity: 1;
        transform: scale(1);
      }
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.installPrompt = e;
      this.showInstallButton = true;
    });
    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);
    navigator.serviceWorker.addEventListener('message', this.messageHandler);
  }

  disconnectedCallback() {
    window.removeEventListener('online', this.onlineHandler);
    window.removeEventListener('offline', this.offlineHandler);
    navigator.serviceWorker.removeEventListener('message', this.messageHandler);
    this.clearSetupTimeout();
    this.clearNotificationSuccessTimeout();
    this.clearAppIntroTimeout();
    this.clearExternalLaunchTimeout();
    this.clearAlertsPromptTimeout();
    if (this.promptTimeoutId) window.clearTimeout(this.promptTimeoutId);
    super.disconnectedCallback();
  }

  protected firstUpdated(): void {
    this.evaluateWelcomeScreen();
    this.handleSplashScreen();

    const urlParams = new URLSearchParams(window.location.search);
    const pageSrc = urlParams.get('pageSrc');
    const notificationUrl = urlParams.get('notificationUrl');
    const targetUrl = pageSrc || notificationUrl;

    if (targetUrl) {
      this.iframeSrc = targetUrl;
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    void this.bootstrapAlertsPrompt();
  }

  private schedulePrompt() {
    // Clear any existing timer so we don't have duplicates
    if (this.promptTimeoutId) window.clearTimeout(this.promptTimeoutId);

    this.showPersonalizePrompt = false; // Ensure it's hidden initially

    // Set 5 second delay
    this.promptTimeoutId = window.setTimeout(() => {
      this.showPersonalizePrompt = true;
    }, 5000);
  }

  private loadAlertsPromptState(): AlertsPromptState {
    try {
      const raw = window.localStorage.getItem(ALERTS_PROMPT_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return {
          snoozeUntil: typeof parsed.snoozeUntil === 'number' ? parsed.snoozeUntil : undefined,
          permanentlyDismissed: !!parsed.permanentlyDismissed,
        };
      }
    } catch (error) {
      console.warn('Failed to read alerts prompt state.', error);
    }
    return {};
  }

  private saveAlertsPromptState(state: AlertsPromptState) {
    try {
      window.localStorage.setItem(ALERTS_PROMPT_STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to store alerts prompt state.', error);
    }
  }

  private clearAlertsPromptState() {
    try {
      window.localStorage.removeItem(ALERTS_PROMPT_STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear alerts prompt state.', error);
    }
  }

  private clearAlertsPromptTimeout() {
    if (this.alertsPromptTimeoutId !== null) {
      window.clearTimeout(this.alertsPromptTimeoutId);
      this.alertsPromptTimeoutId = null;
    }
  }

  private hideAlertsPrompt() {
    this.showAlertsPrompt = false;
    this.clearAlertsPromptTimeout();
  }

  private async bootstrapAlertsPrompt() {
    if (!this.notificationsSupported) return;

    const mode = window.localStorage.getItem('wj-user-mode');
    if (mode === 'browsing') return;

    const state = this.loadAlertsPromptState();
    const now = Date.now();
    if (state.permanentlyDismissed) return;
    if (state.snoozeUntil && state.snoozeUntil > now) return;

    let shouldPrompt = this.shouldPromptForAlerts;
    const userId = this.getClientId();

    try {
      const [preferences, subscriptionStatus] = await Promise.all([
        fetchUserPreferences(userId).catch(() => null),
        fetchSubscriptionStatus(userId).catch(() => null),
      ]);
      const hasActiveSubscription = await this.hasActivePushSubscription();

      if (preferences?.jobTitle && !this.jobTitle) {
        this.jobTitle = preferences.jobTitle;
      }
      if (preferences?.jobLocation && !this.jobLocation) {
        this.jobLocation = preferences.jobLocation;
      }
      if (typeof preferences?.alertsOptIn === 'boolean') {
        this.alertsOptIn = preferences.alertsOptIn;
      }

      const serverHasSubscription = Boolean(subscriptionStatus?.hasSubscription);
      const alertsOptIn = Boolean(preferences?.alertsOptIn);

      // Prompt if they have no active subscription and haven't opted out server-side.
      if (!hasActiveSubscription && !serverHasSubscription && !alertsOptIn) {
        shouldPrompt = true;
      }

      // If server says they want alerts but there's no subscription, prompt to repair.
      if (alertsOptIn && !hasActiveSubscription && !serverHasSubscription) {
        shouldPrompt = true;
      }
    } catch (error) {
      console.warn('Failed to bootstrap alerts prompt.', error);
    }

    if (shouldPrompt && !this.showWelcomeScreen) {
      this.maybeScheduleAlertsPrompt(ALERTS_PROMPT_INITIAL_DELAY_MS);
    } else {
      this.shouldPromptForAlerts = shouldPrompt;
    }
  }

  private async maybeScheduleAlertsPrompt(delayMs = ALERTS_PROMPT_INITIAL_DELAY_MS) {
    if (!this.notificationsSupported) return;

    const state = this.loadAlertsPromptState();
    const now = Date.now();
    if (state.permanentlyDismissed) return;
    if (state.snoozeUntil && state.snoozeUntil > now) return;
    if (this.showWelcomeScreen) return;

    this.clearAlertsPromptTimeout();
    const hasSubscription = await this.hasActivePushSubscription();
    if (hasSubscription) {
      this.clearAlertsPromptState();
      return;
    }

    this.alertsPromptTimeoutId = window.setTimeout(() => {
      this.alertsPromptTimeoutId = null;
      this.showAlertsPrompt = true;
    }, delayMs);
  }

  private handleAlertsPromptSnooze = () => {
    this.notificationError = null;
    this.hideAlertsPrompt();
    const state = this.loadAlertsPromptState();
    this.saveAlertsPromptState({
      ...state,
      snoozeUntil: Date.now() + ALERTS_PROMPT_COOLDOWN_MS,
    });
    this.shouldPromptForAlerts = false;
  };

  private handleAlertsPromptEnable = async () => {
    this.notificationError = null;
    this.hideAlertsPrompt();

    const preferencesSaved = await this.persistPreferences(true);
    if (!preferencesSaved) {
      this.showAlertsPrompt = true;
      return;
    }

    const success = await this.enableNotifications();
    if (success) {
      this.alertsOptIn = true;
      this.shouldPromptForAlerts = false;
      this.clearAlertsPromptState();
      return;
    }

    this.showAlertsPrompt = true;
  };

  /**
   * Handles "I'm hiring or posting a job" option from welcome screen.
   */
  private handleEmployerSelection = () => {
    this.welcomeStage = 'employerComingSoon';
  };

  /**
   * Handles the splash screen timing and transition.
   */
  private handleSplashScreen() {
    setTimeout(() => {
      this.splashFading = true;

      setTimeout(() => {
        this.showSplashScreen = false;
        // Trigger the welcome animations exactly when splash is gone
        this.welcomeAnimActive = true;
      }, 500);
    }, 2500);
  }

  private handleMessage(event: MessageEvent) {
    if (event.data?.type === 'navigate' && event.data?.url) {
      this.iframeSrc = event.data.url;
    }
  }

  render() {
    const showOfflinePanel = this.isOffline || this.iframeFailed;
    const jobLabel = this.jobTitle || 'jobs';
    const locationLabel = this.jobLocation ? ` jobs near ${this.jobLocation}` : '';
    return html`
      <div class="container">
        ${this.showExternalLaunchNotice
          ? html`
              <div class="external-launch" role="status" aria-live="polite">
                <div class="external-launch__card">
                  <div class="external-launch__spinner" aria-hidden="true"></div>
                  <p class="external-launch__title">Opening in your browser</p>
                  <p class="external-launch__subtitle">Hang tight, opening shortly.</p>
                </div>
              </div>
            `
          : null}
        ${this.showSplashScreen
          ? html`
              <div class="splash-screen ${this.splashFading ? 'fade-out' : ''}" aria-hidden=${this.splashFading}>
                <div class="splash-content">
                  <!-- Changed src to whatjobsLogoWhite -->
                  <img src=${whatjobsLogoWhite} class="splash-logo" alt="WhatJobs" />
                  <div class="splash-loader"></div>
                </div>
              </div>
            `
          : null}
        ${this.showWelcomeScreen
          ? html`
              <!-- Updated class attribute -->
              <div class="welcome-overlay ${this.welcomeAnimActive ? 'anim-start' : ''}" role="dialog" aria-modal="true">
                ${this.renderWelcomeContent()}
              </div>
            `
          : null}
        ${this.showInstallButton
          ? html`
              <div class="install-header" role="banner" aria-label="Install WhatJobs">
                <div class="install-copy">
                  <p class="install-title">Install WhatJobs for instant access</p>
                  <p class="install-subtitle">
                    Keep recent searches one tap away and launch faster.
                  </p>
                </div>
                <div class="install-actions">
                  <sl-button class="install-primary" @click=${this.install}>Install now</sl-button>
                  <sl-button class="install-secondary" @click=${() => (this.showInstallButton = false)}>
                    Not now
                  </sl-button>
                </div>
              </div>
            `
          : null}
        ${showOfflinePanel
          ? html`
              <section class="offline-panel" role="status" aria-live="polite">
                <h2>Connection lost</h2>
                <p>Can't reach WhatJobs. Check your connection and try again.</p>
                <sl-button variant="primary" @click=${this.retryConnection} ?disabled=${!navigator.onLine}>
                  Retry
                </sl-button>
              </section>
            `
          : null}
        <div class="personalize-prompt" ?data-open=${this.showPersonalizePrompt && !this.showWelcomeScreen}>
          <p class="personalize-text">Want to personalize your experience?</p>
          <div class="personalize-actions">
            <sl-button variant="default" size="small" @click=${() => this.showPersonalizePrompt = false}>No</sl-button>
            <sl-button variant="primary" size="small" @click=${this.handlePersonalizeClick}>Yes</sl-button>
          </div>
        </div>
        <div class="alerts-prompt" ?data-open=${this.showAlertsPrompt && !this.showWelcomeScreen}>
          <div class="alerts-copy">
            <p class="alerts-title">Turn on job alerts</p>
            <p class="alerts-subtitle">Get alerts for new ${jobLabel}${locationLabel}.</p>
            ${this.notificationError
              ? html`<p class="notification-error" role="alert">${this.notificationError}</p>`
              : null}
          </div>
          <div class="alerts-actions">
            <sl-button size="small" @click=${this.handleAlertsPromptSnooze} ?disabled=${this.isProcessingNotification}>
              Not now
            </sl-button>
            <sl-button
              variant="primary"
              size="small"
              @click=${this.handleAlertsPromptEnable}
              ?disabled=${this.isProcessingNotification}
            >
              Enable alerts
            </sl-button>
          </div>
        </div>
        <main class=${this.appIntroActive ? 'app-main app-main--intro' : 'app-main'} ?hidden=${showOfflinePanel}>
          <iframe
            src=${this.iframeSrc}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            @load=${this.handleIframeLoad}
            @error=${this.handleIframeError}
          ></iframe>
        </main>
      </div>
    `;
  }
  private renderWelcomeContent() {
    const jobLabel = this.jobTitle || 'jobs';
    const locationLabel = this.jobLocation ? ` jobs near ${this.jobLocation}` : '';

    switch (this.welcomeStage) {
      case 'intro':
        return html`
          <section class="welcome-content">
            <img src=${whatjobsLogo} alt="WhatJobs" class="welcome-logo fade-in" width="240" height="110" />
            <h1 class="welcome-headline fade-in fade-in--delay-1">Let's set up your WhatJobs experience</h1>
            <p class="welcome-subheadline fade-in fade-in--delay-2">What brings you here today?</p>
            <div class="welcome-actions">
              <button class="welcome-action fade-in fade-in--delay-2" type="button" @click=${this.handleJobSeekerSelection}>
                I'm looking for a job
              </button>
              <button class="welcome-action fade-in fade-in--delay-3" type="button" @click=${this.handleEmployerSelection}>
                I'm hiring/posting a job
              </button>
              <button
                class="welcome-action fade-in fade-in--delay-4"
                type="button"
                @click=${this.handleJustBrowsing}
              >
                Just browsing
              </button>
            </div>
          </section>
        `;
      case 'employerComingSoon':
        return html`
          <section class="welcome-content fade-in">
            <img src=${whatjobsLogo} alt="WhatJobs" class="welcome-logo" width="240" height="110" />
            <h1 class="welcome-headline">Employer features coming soon.</h1>
            <p class="welcome-subheadline">We're working to bring these to you.</p>
            <div class="form-actions">
              <button class="secondary-button" type="button" @click=${this.handleIntroBack}>Back</button>
            </div>
          </section>
        `;
      case 'jobDetails':
        return html`
          <section class="welcome-content welcome-content--form">
            <img src=${whatjobsLogo} alt="WhatJobs" class="welcome-logo fade-in" width="240" height="110" />
            <h1 class="welcome-headline fade-in fade-in--delay-1">Let's personalize your feed</h1>
            <p class="welcome-subheadline fade-in fade-in--delay-2">
              Add a role and optional location so we can tailor your feed.
            </p>
            <div class="welcome-form fade-in fade-in--delay-3">
              <div class="field">
                <label for="job-title">Job title</label>
                <input
                  id="job-title"
                  type="text"
                  placeholder="e.g. Product Designer"
                  autocomplete="off"
                  required
                  aria-invalid=${this.jobDetailsError ? 'true' : 'false'}
                  .value=${this.jobTitle}
                  @input=${this.handleJobTitleInput}
                />
              </div>
              <div class="field">
                <label for="job-location">Location (optional)</label>
                <input
                  id="job-location"
                  type="text"
                  placeholder="e.g. London, UK"
                  autocomplete="off"
                  .value=${this.jobLocation}
                  @input=${this.handleJobLocationInput}
                />
              </div>
            </div>
            ${this.jobDetailsError ? html`<p class="notification-error">${this.jobDetailsError}</p>` : null}
            <div class="form-actions fade-in fade-in--delay-4">
              <button class="secondary-button" type="button" @click=${this.handleIntroBack}>Back</button>
              <button class="primary-button" type="button" @click=${this.handleJobDetailsContinue}>Continue</button>
            </div>
          </section>
        `;
      case 'alerts': {
        const toggleDisabled = !this.notificationsSupported || this.isProcessingNotification;
        return html`
          <section class="welcome-content welcome-content--form">
            <div class="toggle-card fade-in">
              <div class="toggle-row">
                <div>
                  <h2>Enable job alerts?</h2>
                  <p>Get alerts when new ${jobLabel}${locationLabel} go live.</p>
                </div>
                <label class="toggle-switch">
                  <input
                    type="checkbox"
                    .checked=${this.alertsOptIn}
                    @change=${this.handleAlertsToggle}
                    ?disabled=${toggleDisabled}
                  />
                  <span class="toggle-slider"></span>
                </label>
              </div>
              <p class="welcome-content-p">We'll only send alerts that match your WhatJobs preferences. Change this anytime.</p>
            </div>
            ${!this.notificationsSupported
              ? html`
                  <p class="toggle-support-note fade-in fade-in--delay-1">
                    Push alerts aren't supported on this device; you can continue without them.
                  </p>
                `
              : null}
            ${this.notificationError ? html`<p class="notification-error">${this.notificationError}</p>` : null}
            ${this.isProcessingNotification
              ? html`
                  <p class="notification-progress">
                    <span class="progress-dot"></span>
                    Enabling alerts...
                  </p>
                `
              : null}
            <div class="form-actions fade-in fade-in--delay-2">
              <button
                class="secondary-button"
                type="button"
                @click=${this.handleAlertsBack}
                ?disabled=${this.isProcessingNotification}
              >
                Back
              </button>
              <button
                class="primary-button"
                type="button"
                @click=${this.handleAlertsContinue}
                ?disabled=${this.isProcessingNotification || (this.alertsOptIn && !this.notificationsSupported)}
              >
                Continue
              </button>
            </div>
          </section>
        `;
      }
      case 'notificationsSuccess':
        return html`
          <section class="success-content fade-in">
            <div class="success-badge">
              <span class="success-check" aria-hidden="true">
                <svg viewBox="0 0 24 24" role="presentation" width="100%" height="100%">
                  <path
                    d="M5 13.5 9.5 18 19 6"
                    fill="none"
                    stroke="#ffffff"
                    stroke-width="3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ></path>
                </svg>
              </span>
            </div>
            <h2 class="success-title">Notifications enabled</h2>
            <p class="success-copy">We'll let you know when new ${jobLabel}${locationLabel} go live.</p>
          </section>
        `;
      case 'loading':
      default:
        return html`
          <section class="setup-content fade-in">
            <div class="setup-spinner" role="presentation"></div>
            <p class="setup-title">Setting up your experience...</p>
            <ul class="setup-steps" aria-live="polite">
              <li class="setup-step fade-in fade-in--delay-1">Optimizing feed preferences</li>
              <li class="setup-step fade-in fade-in--delay-2">Preparing offline support</li>
              <li class="setup-step fade-in fade-in--delay-3">Connecting to WhatJobs services</li>
            </ul>
          </section>
        `;
    }
  }

  async install() {
    if (this.installPrompt) {
      this.installPrompt.prompt();
      const { outcome } = await this.installPrompt.userChoice;
      if (outcome === 'accepted') {
        this.showInstallButton = false;
      }
    }
  }

  private handleOnline() {
    this.isOffline = false;
    this.iframeFailed = false;
    this.clearExternalLaunchTimeout();
    this.reloadIframe();
  }

  private handleOffline() {
    this.isOffline = true;
    this.clearExternalLaunchTimeout();
  }

  private handleIframeLoad() {
    this.iframeFailed = false;
    this.clearExternalLaunchTimeout();
  }

  private handleIframeError() {
    if (!navigator.onLine) {
      this.isOffline = true;
      this.iframeFailed = true;
      return;
    }
    if (this.iframeSrc) {
      this.triggerExternalBrowserOpen(this.iframeSrc);
      return;
    }
    this.iframeFailed = true;
  }

  private triggerExternalBrowserOpen(url: string) {
    this.iframeFailed = false;
    this.showExternalLaunchNotice = true;
    this.clearExternalLaunchTimeout();
    this.externalLaunchTimeoutId = window.setTimeout(() => {
      window.open(url, '_blank', 'noopener,noreferrer');
      this.showExternalLaunchNotice = false;
      this.externalLaunchTimeoutId = null;
    }, 3000);
  }

  private retryConnection() {
    if (!navigator.onLine) {
      return;
    }
    this.iframeFailed = false;
    this.reloadIframe();
  }

  private reloadIframe() {
    const url = new URL(this.iframeSrc);
    url.searchParams.set('pwaReload', String(Date.now()));
    this.iframeSrc = url.toString();
  }

  private evaluateWelcomeScreen() {
    if (!this.isStandaloneExperience()) return;

    try {
      const hasSeenWelcome = window.localStorage.getItem('wj-welcome-screen');
      const userMode = window.localStorage.getItem('wj-user-mode');

      if (!hasSeenWelcome) {
        this.showWelcomeScreen = true;
        this.welcomeStage = 'intro';
        this.jobTitle = '';
        this.jobLocation = '';
        this.alertsOptIn = this.notificationsSupported;
        this.clearNotificationMessages();
      } else if (userMode === 'browsing') {
        this.showPersonalizePrompt = true;
        this.schedulePrompt();
      }
    } catch (error) {
      console.warn('Failed to access localStorage for welcome screen.', error);
    }
  }

  private handleJustBrowsing = () => {
    try {
      window.localStorage.setItem('wj-user-mode', 'browsing');
    } catch (e) { }

    this.transitionToLoading();
    // Triggers the 5s delay after they click "Just browsing"
    this.schedulePrompt();
  };

  private handleJobSeekerSelection = () => {
     try {
      window.localStorage.setItem('wj-user-mode', 'jobseeker');
    } catch (e) { }

    this.showPersonalizePrompt = false;
    this.clearNotificationMessages();
    this.clearNotificationSuccessTimeout();
    this.jobTitle = '';
    this.jobLocation = '';
    this.alertsOptIn = this.notificationsSupported;
    this.welcomeStage = 'jobDetails';
  };

  private handlePersonalizeClick = () => {
    this.showPersonalizePrompt = false;
    if (this.promptTimeoutId) window.clearTimeout(this.promptTimeoutId);

    this.showWelcomeScreen = true;
    this.welcomeStage = 'jobDetails';
    this.jobTitle = '';
    this.jobLocation = '';
    this.alertsOptIn = this.notificationsSupported;

    // Reset and re-trigger animation for smooth entrance
    this.welcomeAnimActive = false;
    setTimeout(() => { this.welcomeAnimActive = true; }, 50);
  };

  private handleIntroBack = () => {
    const userMode = window.localStorage.getItem('wj-user-mode');

    // If user is in "browsing" mode, they came from the prompt/app
    // so we close the welcome screen completely.
    if (userMode === 'browsing') {
      this.showWelcomeScreen = false;
      this.schedulePrompt(); // Will slide in again after 5 seconds
      return;
    }

    // Normal flow: go back to the 3 options
    this.clearNotificationMessages();
    this.clearNotificationSuccessTimeout();
    this.welcomeStage = 'intro';
  };

  private handleJobDetailsContinue = () => {
    this.clearNotificationMessages();
    const trimmedTitle = this.jobTitle.trim();
    if (!trimmedTitle) {
      this.jobDetailsError = 'Enter a job title to continue.';
      return;
    }
    this.jobTitle = trimmedTitle;
    this.jobLocation = this.jobLocation.trim();
    this.jobDetailsError = null;
    this.alertsOptIn = this.notificationsSupported;
    this.welcomeStage = 'alerts';
  };

  private handleAlertsBack = () => {
    this.clearNotificationMessages();
    this.clearNotificationSuccessTimeout();
    this.welcomeStage = 'jobDetails';
  };

  private handleAlertsToggle = (event: Event) => {
    const input = event.currentTarget as HTMLInputElement | null;
    this.alertsOptIn = !!input?.checked;
    if (!this.alertsOptIn) {
      this.clearNotificationMessages();
    }
  };

  private handleJobTitleInput = (event: Event) => {
    const input = event.currentTarget as HTMLInputElement | null;
    this.jobTitle = input?.value ?? '';
    this.jobDetailsError = null;
  };

  private handleJobLocationInput = (event: Event) => {
    const input = event.currentTarget as HTMLInputElement | null;
    this.jobLocation = input?.value ?? '';
  };

  private handleAlertsContinue = async () => {
    this.isProcessingNotification = true;
    this.clearNotificationMessages();

    // Mark them as jobseeker now so the prompt doesn't appear on restart
    try {
      window.localStorage.setItem('wj-user-mode', 'jobseeker');
    } catch (e) { /* ignore */ }

    const preferencesSaved = await this.persistPreferences(this.alertsOptIn);

    // ... rest of the function remains the same ...
    if (!preferencesSaved) {
      this.isProcessingNotification = false;
      return;
    }

    this.shouldPromptForAlerts = this.notificationsSupported && !this.alertsOptIn;

    if (this.alertsOptIn && this.notificationsSupported) {
      const success = await this.enableNotifications();
      if (success) {
        this.clearAlertsPromptState();
        this.shouldPromptForAlerts = false;
        this.welcomeStage = 'notificationsSuccess';
        this.clearNotificationSuccessTimeout();
        this.notificationSuccessTimeoutId = window.setTimeout(() => {
          this.transitionToLoading();
        }, WELCOME_TIMINGS.notificationSuccessDuration);
        return;
      }
      this.isProcessingNotification = false;
      return;
    }

    this.transitionToLoading();
    this.isProcessingNotification = false;
  };

  private transitionToLoading() {
    this.markWelcomeSeen();
    this.clearNotificationMessages();
    this.welcomeStage = 'loading';
    this.clearSetupTimeout();
    this.clearNotificationSuccessTimeout();
    this.clearAppIntroTimeout();
    this.setupTimeoutId = window.setTimeout(() => {
      this.appIntroActive = true;
      this.showWelcomeScreen = false;
      this.welcomeStage = 'intro';
      this.setupTimeoutId = null;
      if (this.shouldPromptForAlerts) {
        void this.maybeScheduleAlertsPrompt(ALERTS_PROMPT_INITIAL_DELAY_MS);
      }
      this.appIntroTimeoutId = window.setTimeout(() => {
        this.appIntroActive = false;
        this.appIntroTimeoutId = null;
      }, WELCOME_TIMINGS.appIntroDuration);
    }, WELCOME_TIMINGS.loadingDuration);
  }

  private markWelcomeSeen() {
    try {
      window.localStorage.setItem('wj-welcome-screen', 'seen');
    } catch (error) {
      console.warn('Failed to persist welcome screen dismissal.', error);
    }
  }

  private getClientId(): string {
    if (this.clientId) {
      return this.clientId;
    }
    const storageKey = 'wj-client-id';
    try {
      const existing = window.localStorage.getItem(storageKey);
      if (existing) {
        this.clientId = existing;
        return existing;
      }
      const freshId =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `wj-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      window.localStorage.setItem(storageKey, freshId);
      this.clientId = freshId;
      return freshId;
    } catch (error) {
      console.warn('Failed to persist client id.', error);
      const fallback = `wj-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      this.clientId = fallback;
      return fallback;
    }
  }

  private async persistPreferences(alertsOptIn: boolean): Promise<boolean> {
    const jobTitle = this.jobTitle.trim();
    const jobLocation = this.jobLocation.trim();
    try {
      await saveUserPreferences({
        userId: this.getClientId(),
        jobTitle,
        jobLocation: jobLocation || undefined,
        alertsOptIn,
      });
      return true;
    } catch (error) {
      this.notificationError =
        error instanceof Error ? error.message : 'Couldn\'t save your preferences right now.';
      return false;
    }
  }

  private clearSetupTimeout() {
    if (this.setupTimeoutId !== null) {
      window.clearTimeout(this.setupTimeoutId);
      this.setupTimeoutId = null;
    }
  }

  private clearNotificationSuccessTimeout() {
    if (this.notificationSuccessTimeoutId !== null) {
      window.clearTimeout(this.notificationSuccessTimeoutId);
      this.notificationSuccessTimeoutId = null;
    }
  }

  private clearAppIntroTimeout() {
    if (this.appIntroTimeoutId !== null) {
      window.clearTimeout(this.appIntroTimeoutId);
      this.appIntroTimeoutId = null;
    }
  }

  private clearExternalLaunchTimeout() {
    if (this.externalLaunchTimeoutId !== null) {
      window.clearTimeout(this.externalLaunchTimeoutId);
      this.externalLaunchTimeoutId = null;
    }
    this.showExternalLaunchNotice = false;
  }

  private isStandaloneExperience(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const isStandaloneMatch = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
    const isIOSStandalone = (navigator as any)?.standalone === true;
    return isStandaloneMatch || isIOSStandalone;
  }

  private clearNotificationMessages() {
    this.notificationError = null;
    this.jobDetailsError = null;
  }

  private async ensureNotificationPermission(): Promise<NotificationPermission> {
    if (!this.notificationsSupported) {
      return 'denied';
    }
    if (this.notificationPermission !== 'default') {
      return this.notificationPermission;
    }
    const result = await Notification.requestPermission();
    this.notificationPermission = result;
    return result;
  }

  private async hasActivePushSubscription(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      return false;
    }
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      return Boolean(subscription);
    } catch (error) {
      console.warn('Failed to check push subscription.', error);
      return false;
    }
  }

  private enableNotifications = async (): Promise<boolean> => {
    let success = false;
    this.clearNotificationMessages();
    this.isProcessingNotification = true;
    try {
      const permission = await this.ensureNotificationPermission();
      if (permission !== 'granted') {
        this.notificationError = 'Notifications are blocked. Turn them on in your browser.';
        return false;
      }

      const registration = await this.getServiceWorkerRegistration();
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        const publicKey = await fetchPublicKey();
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      const normalizedSubscription = this.normalizeSubscription(subscription);

      await saveSubscription({
        subscription: normalizedSubscription,
        userId: this.getClientId(),
        metadata: {
          jobTitle: this.jobTitle.trim(),
          jobLocation: this.jobLocation.trim() || null,
        },
      });
      success = true;
    } catch (error) {
      this.notificationError =
        error instanceof Error ? error.message : 'Couldn\'t enable notifications right now.';
    } finally {
      this.isProcessingNotification = false;
    }
    return success;
  };

  private async getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
    if (!('serviceWorker' in navigator)) {
      throw new Error('This browser doesn\'t support service workers.');
    }

    const existing = await navigator.serviceWorker.getRegistration();
    if (existing) {
      return this.waitForActiveServiceWorker(existing);
    }

    try {
      // Ensure we have a registration even on first-run before the page is controlled.
      const swUrl = this.getServiceWorkerUrl();
      const registration = await navigator.serviceWorker.register(swUrl, { type: 'module' });
      return this.waitForActiveServiceWorker(registration);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Couldn\'t register the service worker.';
      throw new Error(`Couldn't register the service worker: ${message}`);
    }
  }

  private async waitForActiveServiceWorker(registration: ServiceWorkerRegistration): Promise<ServiceWorkerRegistration> {
    if (registration.active) {
      return registration;
    }

    const installingWorker = registration.installing || registration.waiting;
    if (installingWorker && installingWorker.state !== 'activated') {
      await new Promise<void>((resolve, reject) => {
        const handleStateChange = () => {
          if (installingWorker.state === 'activated') {
            installingWorker.removeEventListener('statechange', handleStateChange);
            resolve();
          } else if (installingWorker.state === 'redundant') {
            installingWorker.removeEventListener('statechange', handleStateChange);
            reject(new Error('Service worker became redundant during installation.'));
          }
        };
        installingWorker.addEventListener('statechange', handleStateChange);
      });
    }

    // Ensure we return a registration with an active worker controlling the page.
    const readyRegistration = await navigator.serviceWorker.ready;
    return readyRegistration;
  }

  private getServiceWorkerUrl(): string {
    // VitePWA serves a dev service worker at /dev-sw.js?dev-sw; production uses /sw.js
    return import.meta.env.DEV ? '/dev-sw.js?dev-sw' : '/sw.js';
  }

  private normalizeSubscription(subscription: PushSubscription): PushSubscriptionJSON {
    const raw = subscription.toJSON();
    const keys = raw?.keys ?? {};

    if (!raw?.endpoint) {
      throw new Error('Failed to register push subscription (missing endpoint).');
    }

    if (!keys.p256dh || !keys.auth) {
      throw new Error('Failed to register push subscription (missing encryption keys).');
    }

    return {
      endpoint: raw.endpoint,
      expirationTime: raw.expirationTime ?? null,
      keys: {
        p256dh: keys.p256dh,
        auth: keys.auth,
      },
    };
  }




}
